/**
 * Colorizes a raw single-band u8 mosaic raster into a canvas data: URL —
 * same NWS palette LUT (palette.ts) and pixel-format contract as
 * renderFrame.ts's per-station decodeFrame, plus an optional display floor
 * (fusionCompositor.ts's PRECIP_FLOOR_RAW/NO_FLOOR_RAW — the same "precip
 * only" toggle the WebGL fusion shader applies before blending). There's no
 * blend step here: mosaic pixels are already resolved, single-writer values
 * by construction (workers/CONTRACTS.md's "z7 paste alignment" — a mosaic
 * byte is always some station's own exact source byte, never a blend), so
 * mosaic rendering only ever needs to colorize, not merge — the floor is
 * just a straight per-pixel cutoff, applied client-side same as the raw
 * decoded value always has been for station drill-down.
 *
 * Split into a pure part (colorizeRawBand, below) and a DOM part
 * (rasterToDataUrl): vitest's `environment: "node"` config (vite.config.ts)
 * has no `document`/canvas, which is why renderFrame.ts's equivalent loop
 * has never had direct unit coverage — splitting the math out here avoids
 * repeating that testability gap for the mosaic path.
 */
import { PALETTE_LUT } from "./palette";

/** Pure raw-band -> RGBA colorization, no DOM dependency. `floorRaw` uses
 * the same raw-quantized-byte units as fusionCompositor.ts's
 * PRECIP_FLOOR_RAW/NO_FLOOR_RAW — a value below it renders fully
 * transparent. This always includes the nodata sentinel (raw === 0)
 * whenever a real floor is in effect (floorRaw >= 1); with NO_FLOOR_RAW (0)
 * only true nodata is excluded, matching renderFrame.ts's unfloored
 * station-drill-down behavior exactly (v=0 stays [0,0,0,0] either way,
 * since PALETTE_LUT's own index 0 is already transparent). */
export function colorizeRawBand(raw: ArrayLike<number>, floorRaw: number): Uint8ClampedArray<ArrayBuffer> {
  const rgba = new Uint8ClampedArray(raw.length * 4);
  for (let i = 0; i < raw.length; i++) {
    const v = raw[i] & 0xff;
    if (v < floorRaw) continue; // stays [0,0,0,0] — transparent, already zeroed
    const o = i * 4;
    const li = v * 4;
    rgba[o] = PALETTE_LUT[li];
    rgba[o + 1] = PALETTE_LUT[li + 1];
    rgba[o + 2] = PALETTE_LUT[li + 2];
    rgba[o + 3] = PALETTE_LUT[li + 3];
  }
  return rgba;
}

/** DOM glue: paints `rgba` (colorizeRawBand's output) into a canvas and
 * returns a data: URL — same synchronous toDataURL() pattern renderFrame.ts
 * uses, and for the same reason (that file's doc comment): a placeholder
 * image source's async load can race a later updateImage({image}) call, so
 * a synchronous data: URL sidesteps that entirely. */
export function rasterToDataUrl(rgba: Uint8ClampedArray<ArrayBuffer>, width: number, height: number): string {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  ctx.putImageData(new ImageData(rgba, width, height), 0, 0);
  return canvas.toDataURL("image/png");
}

export function colorizeMosaicRaster(raw: ArrayLike<number>, width: number, height: number, floorRaw: number): string {
  return rasterToDataUrl(colorizeRawBand(raw, floorRaw), width, height);
}
